import React, { useState } from 'react';
import { Sparkles, ChevronDown, ChevronUp, Tag, ArrowRight } from 'lucide-react';

/**
 * Bundle Tags™ informational panel.
 * Collapsible, remembers state per-browser in localStorage.
 * Standalone component so any page (Dashboard, Members, Profile) can drop it in.
 *
 * Props:
 *   onManageTags?: () => void  — optional; renders the "Manage Your Tags" CTA when provided
 *   className?: string         — extra tailwind classes for spacing overrides
 */
export const BundleTagsInfoCard = ({ onManageTags, className = '' }) => {
  const storageKey = 'bundle_tags_info_collapsed';
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(storageKey) === '1'; } catch { return false; }
  });

  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    try { localStorage.setItem(storageKey, next ? '1' : '0'); } catch { /* ignore */ }
  };

  const uses = [
    { emoji: '🤝', text: 'Find golfers who share your interests' },
    { emoji: '💼', text: 'Expand your professional network' },
    { emoji: '✈️', text: 'Connect with golfers while traveling' },
    { emoji: '🎓', text: 'Meet fellow alumni' },
    { emoji: '⭐', text: 'Find fraternity and sorority members' },
    { emoji: '🦅', text: 'Connect with veterans and military members' },
    { emoji: '🏆', text: 'Discover tournament players and golf groups' },
    { emoji: '🌎', text: 'Build relationships across the country' },
  ];

  return (
    <div
      data-testid="bundle-tags-info-card"
      className={`relative overflow-hidden rounded-2xl border border-emerald-100 shadow-sm ${className}`}
      style={{
        background:
          'linear-gradient(135deg, #ffffff 0%, #f0fdf4 60%, #ecfdf5 100%)',
      }}
    >
      {/* Decorative background accent */}
      <div className="pointer-events-none absolute -top-16 -right-16 w-64 h-64 rounded-full bg-emerald-500/10 blur-3xl" aria-hidden="true" />
      <div className="pointer-events-none absolute -bottom-20 -left-16 w-64 h-64 rounded-full bg-bundle-gold/10 blur-3xl" aria-hidden="true" />

      {/* Header row (always visible) */}
      <button
        type="button"
        onClick={toggle}
        aria-expanded={!collapsed}
        data-testid="bundle-tags-info-toggle"
        className="relative w-full flex items-center justify-between gap-3 px-5 sm:px-6 py-4 text-left"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-emerald-600" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-heading font-bold text-bundle-green text-lg sm:text-xl leading-tight">Bundle Tags<span className="text-xs align-super">™</span></h2>
              <span className="hidden sm:inline text-[11px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">Members-Only</span>
            </div>
            <p className="text-sm text-gray-600 italic mt-0.5 truncate">Your Interests. Your Network. Your Community.</p>
          </div>
        </div>
        <div className="flex-shrink-0 text-emerald-700 flex items-center gap-1 text-xs font-bold">
          <span className="hidden sm:inline">{collapsed ? 'Learn more' : 'Hide'}</span>
          {collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
        </div>
      </button>

      {/* Expandable body */}
      {!collapsed && (
        <div className="relative px-5 sm:px-6 pb-6 pt-1">
          <p className="text-sm sm:text-[15px] text-gray-700 leading-relaxed max-w-3xl">
            Bundle Tags help you connect with members who share your profession, military service, alma mater, fraternity or sorority, hobbies, favorite teams, travel destinations, and more.
          </p>
          <p className="text-sm sm:text-[15px] text-gray-700 leading-relaxed max-w-3xl mt-2">
            Your selected tags personalize your Bundle Golf Club experience by helping you discover golfers with shared interests and meaningful connections.
          </p>

          <div className="mt-5">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-emerald-800 mb-2.5">Use Bundle Tags to:</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
              {uses.map(({ emoji, text }) => (
                <div key={text} className="flex items-start gap-2.5 text-sm text-gray-800">
                  <span aria-hidden="true" className="text-base leading-6 flex-shrink-0">{emoji}</span>
                  <span className="leading-6">{text}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-6 rounded-xl bg-white/70 border border-emerald-100 p-4 sm:p-5">
            <h3 className="font-heading font-bold text-bundle-green text-base mb-2">Your Tags Work For You</h3>
            <p className="text-sm text-gray-700 leading-relaxed">
              Every tag you select helps introduce you to golfers with similar backgrounds, interests, and experiences—making every connection more meaningful.
            </p>
            <p className="text-sm text-gray-700 leading-relaxed mt-2">
              Whether you&rsquo;re looking for a business connection, a travel golf partner, or someone who simply shares your passion for the game, Bundle Tags help you find your community.
            </p>
            <p className="text-sm text-emerald-800 font-semibold italic mt-3">
              Golf is better when you play with people who share your interests.
            </p>
          </div>

          {onManageTags && (
            <div className="mt-5 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={onManageTags}
                data-testid="bundle-tags-manage-btn"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-bundle-green hover:bg-bundle-green-dark text-white font-bold text-sm shadow-sm transition-colors"
              >
                <Tag className="w-4 h-4" /> Manage Your Tags
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BundleTagsInfoCard;
